import { useEffect, useState } from 'react'
import { Typography } from '@material-ui/core'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import { useFormikContext } from 'formik'
import { EDatasheetFields, NOT_APPLICABLE_FIELD_VALUE } from 'core/common/constants/datasheet'
import i18n from 'core/common/i18n'
import { getErrorMessage, validateField } from 'core/common/utils/validation'
import Checkbox from 'core/components/Checkbox'
import { FormControlLabel, Callout } from 'core/components/Core'
import TextField from 'core/components/TextField'
import * as S from './style'
import { TInitialValues, RegulatoryInformationFieldsProps } from './types'

const RegulatoryInformationFields = ({
  onChange,
  resetFields,
}: RegulatoryInformationFieldsProps) => {
  const { values, handleChange, setFieldValue, handleBlur, errors, touched, submitCount } =
    useFormikContext<TInitialValues>()
  const { anvisa, mapa } = values
  const [anvisaNotApply, setAnvisaNotApply] = useState(anvisa === NOT_APPLICABLE_FIELD_VALUE)
  const [mapaNotApply, setMapaNotApply] = useState(mapa === NOT_APPLICABLE_FIELD_VALUE)

  useEffect(() => {
    if (resetFields) {
      setAnvisaNotApply(anvisa === NOT_APPLICABLE_FIELD_VALUE)
      setMapaNotApply(mapa === NOT_APPLICABLE_FIELD_VALUE)
    }
  }, [resetFields, anvisa, mapa])

  const handleChangeFieldApplicability = (
    field: EDatasheetFields,
    checked: boolean,
    setState: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setState(checked)
    onChange()

    if (checked) {
      setFieldValue(field, NOT_APPLICABLE_FIELD_VALUE)
      return
    }

    setFieldValue(field, '')
  }

  return (
    <>
      <Callout color="primary" className="py-2 px-3 mb-3 align-center">
        <InfoOutlinedIcon fontSize="small" color="primary" className="mr-1" />
        <Typography component="span" variant="body2" color="textPrimary">
          {i18n.t('productNew.regulatoryInformation.notice')}
        </Typography>
      </Callout>

      <div className="d-flex">
        <div className="d-flex flex-column mr-4 flex-1">
          <div className="d-flex mb-3">
            <TextField
              fullWidth
              name={EDatasheetFields.Anvisa}
              variant="outlined"
              label={i18n.t('productNew.regulatoryInformation.anvisaLabel')}
              disabled={anvisaNotApply}
              onChange={e => {
                handleChange(e)
                onChange()
              }}
              value={anvisa}
              onBlur={handleBlur}
              error={validateField(EDatasheetFields.Anvisa, errors, touched, submitCount > 0)}
              helperText={getErrorMessage(EDatasheetFields.Anvisa, errors, touched)}
            />
            <S.LightTooltip
              title={i18n.t('productNew.regulatoryInformation.anvisaTooltip')}
              placement="top"
            >
              <HelpOutlineIcon style={{ marginLeft: 4, fontSize: 16 }} color="primary" />
            </S.LightTooltip>
          </div>

          <div>
            <FormControlLabel
              control={<Checkbox checked={anvisaNotApply} />}
              label={i18n.t('productNew.regulatoryInformation.notApply')}
              onChange={(event, checked) =>
                handleChangeFieldApplicability(EDatasheetFields.Anvisa, checked, setAnvisaNotApply)
              }
              value={anvisaNotApply}
            />
          </div>
        </div>

        <div className="d-flex flex-column flex-1">
          <div className="d-flex mb-3">
            <TextField
              fullWidth
              name={EDatasheetFields.Mapa}
              variant="outlined"
              label={i18n.t('productNew.regulatoryInformation.mapaLabel')}
              disabled={mapaNotApply}
              onChange={e => {
                handleChange(e)
                onChange()
              }}
              value={mapa}
              onBlur={handleBlur}
              error={validateField(EDatasheetFields.Mapa, errors, touched, submitCount > 0)}
              helperText={getErrorMessage(EDatasheetFields.Mapa, errors, touched)}
            />
            <S.LightTooltip
              title={i18n.t('productNew.regulatoryInformation.mapaTooltip')}
              placement="top"
            >
              <HelpOutlineIcon style={{ marginLeft: 4, fontSize: 16 }} color="primary" />
            </S.LightTooltip>
          </div>

          <div>
            <FormControlLabel
              control={<Checkbox checked={mapaNotApply} />}
              label={i18n.t('productNew.regulatoryInformation.notApply')}
              onChange={(event, checked) =>
                handleChangeFieldApplicability(EDatasheetFields.Mapa, checked, setMapaNotApply)
              }
              value={mapaNotApply}
            />
          </div>
        </div>
      </div>
    </>
  )
}

export default RegulatoryInformationFields
